import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Check, Trash2, RotateCcw, Command } from 'lucide-react';
import { BACKEND_URL } from '../config/api';
import { getAuthHeaders } from '../context/AuthContext';

const MARKER_RADIUS_IDLE = 20;
const MARKER_RADIUS_ACTIVE = 50;
const HIT_RADIUS = 36;
const PICKER_MAX_COLORS = 999;

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const WHEEL_ZOOM_SPEED = 0.002;

/**
 * MultiColorPickerModal - Touch-friendly multi-color selection from an image
 * 
 * Mobile gestures:
 * - One finger: select/drag/remove color markers
 * - Two fingers: pinch to zoom, drag to pan
 * 
 * Desktop gestures:
 * - Click/drag: select/drag/remove color markers
 * - Mouse wheel: zoom (centered on cursor)
 * - Ctrl/Cmd + drag: pan
 */
const MultiColorPickerModal = ({ imageUrl, existingColors = [], onConfirm, onClose }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [proxyImageUrl, setProxyImageUrl] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [markers, setMarkers] = useState([]);
  const [activeMarker, setActiveMarker] = useState(null);
  const [dragPreviewColor, setDragPreviewColor] = useState(null);
  
  // Zoom & Pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  
  // Detect if device is touch-capable and if Mac
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMac, setIsMac] = useState(false);
  
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    setIsMac(navigator.platform.toUpperCase().indexOf('MAC') >= 0);
  }, []);
  
  // Gesture tracking
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null); // { type: 'select' | 'pinch' | 'pan', ... }
  
  const availableSlots = PICKER_MAX_COLORS - existingColors.length;
  const canAddMore = markers.length < availableSlots;

  // Fetch image via backend proxy to avoid CORS issues with canvas
  useEffect(() => {
    if (!imageUrl) return;
    let cancelled = false;
    const proxyUrl = `${BACKEND_URL}/api/stash/proxy-image?url=${encodeURIComponent(imageUrl)}`;
    const headers = getAuthHeaders();
    fetch(proxyUrl, { credentials: 'include', headers })
      .then(resp => {
        if (!resp.ok) throw new Error('Proxy fetch failed');
        return resp.blob();
      })
      .then(blob => {
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(blob);
        setProxyImageUrl(blobUrl);
      })
      .catch(() => {
        if (cancelled) return;
        // Fallback: try direct URL
        setProxyImageUrl(imageUrl);
      });
    return () => { cancelled = true; };
  }, [imageUrl]);

  // Draw image on canvas
  const drawImage = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    
    const ctx = canvas.getContext('2d');
    const container = containerRef.current;
    const containerWidth = container?.clientWidth || window.innerWidth - 16;
    
    const maxW = Math.min(containerWidth - 16, 1200);
    const maxH = Math.min(window.innerHeight - 320, 500);
    const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1.5);
    
    const newWidth = Math.round(img.naturalWidth * scale);
    const newHeight = Math.round(img.naturalHeight * scale);
    
    canvas.width = newWidth;
    canvas.height = newHeight;
    setCanvasSize({ width: newWidth, height: newHeight });
    
    ctx.drawImage(img, 0, 0, newWidth, newHeight);
    setImageLoaded(true);
  }, []);

  // Get color at canvas coordinates
  const getColorAtPoint = useCallback((canvasX, canvasY) => {
    const canvas = canvasRef.current;
    if (!canvas) return '#000000';
    
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const x = Math.max(0, Math.min(Math.round(canvasX), canvas.width - 1));
    const y = Math.max(0, Math.min(Math.round(canvasY), canvas.height - 1));
    
    const pixel = ctx.getImageData(x, y, 1, 1).data;
    return '#' + [pixel[0], pixel[1], pixel[2]]
      .map(v => v.toString(16).padStart(2, '0'))
      .join('');
  }, []);

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback((clientX, clientY) => {
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return { x: 0, y: 0 };
    
    const rect = wrapper.getBoundingClientRect();
    const screenCenterX = rect.left + rect.width / 2;
    const screenCenterY = rect.top + rect.height / 2;
    
    const touchOffsetX = clientX - screenCenterX;
    const touchOffsetY = clientY - screenCenterY;
    
    const canvasOffsetX = touchOffsetX / zoom;
    const canvasOffsetY = touchOffsetY / zoom;
    
    const canvasX = canvasOffsetX + canvas.width / 2;
    const canvasY = canvasOffsetY + canvas.height / 2;
    
    return { x: canvasX, y: canvasY };
  }, [zoom]);

  // Check if point is within a marker
  const findMarkerAtPoint = useCallback((canvasX, canvasY) => {
    const hitRadius = HIT_RADIUS / zoom;
    
    for (let i = markers.length - 1; i >= 0; i--) {
      const marker = markers[i];
      const dx = canvasX - marker.x;
      const dy = canvasY - marker.y;
      if (Math.sqrt(dx * dx + dy * dy) <= hitRadius) {
        return marker;
      }
    }
    return null;
  }, [markers, zoom]);

  // Clamp pan to keep image visible
  const clampPan = useCallback((newPan, newZoom) => {
    if (newZoom <= 1) return { x: 0, y: 0 };
    const maxPanX = canvasSize.width * (newZoom - 1) / 2;
    const maxPanY = canvasSize.height * (newZoom - 1) / 2;
    return {
      x: Math.max(-maxPanX, Math.min(maxPanX, newPan.x)),
      y: Math.max(-maxPanY, Math.min(maxPanY, newPan.y))
    };
  }, [canvasSize]);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Get distance between two pointers
  const getPointersDistance = useCallback(() => {
    const pts = Array.from(pointersRef.current.values());
    if (pts.length < 2) return 0;
    const dx = pts[1].clientX - pts[0].clientX;
    const dy = pts[1].clientY - pts[0].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  // Get center point between two pointers
  const getPointersCenter = useCallback(() => {
    const pts = Array.from(pointersRef.current.values());
    if (pts.length < 2) return { x: 0, y: 0 };
    return {
      x: (pts[0].clientX + pts[1].clientX) / 2,
      y: (pts[0].clientY + pts[1].clientY) / 2
    };
  }, []);

  // Cancel any in-progress selection
  const cancelSelection = useCallback(() => {
    if (activeMarker?.isNew) {
      setMarkers(prev => prev.filter(m => m.id !== activeMarker.id));
    }
    setActiveMarker(null);
    setDragPreviewColor(null);
  }, [activeMarker]);

  // ============ MOUSE WHEEL ZOOM (Desktop) ============
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    
    const wrapper = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return;
    
    // Calculate zoom change
    const delta = -e.deltaY * WHEEL_ZOOM_SPEED;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom + delta * zoom));
    
    if (newZoom === zoom) return;
    
    // Get cursor position relative to wrapper center
    const rect = wrapper.getBoundingClientRect();
    const cursorX = e.clientX - (rect.left + rect.width / 2);
    const cursorY = e.clientY - (rect.top + rect.height / 2);
    
    // Adjust pan to zoom toward cursor position
    // This keeps the point under the cursor stationary
    const zoomRatio = newZoom / zoom;
    const newPanX = cursorX - (cursorX - pan.x) * zoomRatio;
    const newPanY = cursorY - (cursorY - pan.y) * zoomRatio;
    
    const clampedPan = clampPan({ x: newPanX, y: newPanY }, newZoom);
    
    setZoom(newZoom);
    setPan(clampedPan);
  }, [zoom, pan, clampPan]);

  // ============ UNIFIED POINTER HANDLERS ============
  
  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Track this pointer
    pointersRef.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
    const pointerCount = pointersRef.current.size;
    
    // Desktop: Ctrl/Cmd + click = start pan
    if (pointerCount === 1 && (e.ctrlKey || e.metaKey)) {
      gestureRef.current = {
        type: 'pan',
        startX: e.clientX,
        startY: e.clientY,
        startPan: { ...pan }
      };
      return;
    }
    
    // Two fingers (mobile): start pinch/pan gesture
    if (pointerCount === 2) {
      cancelSelection();
      
      gestureRef.current = {
        type: 'pinch',
        startDistance: getPointersDistance(),
        startZoom: zoom,
        startCenter: getPointersCenter(),
        startPan: { ...pan }
      };
      return;
    }
    
    // One finger/click: start selection (only if no gesture in progress)
    if (pointerCount === 1 && !gestureRef.current) {
      const { x, y } = screenToCanvas(e.clientX, e.clientY);
      const existing = findMarkerAtPoint(x, y);
      
      if (existing) {
        setActiveMarker({
          id: existing.id,
          startX: e.clientX,
          startY: e.clientY,
          moved: false,
          isNew: false
        });
        setDragPreviewColor(existing.color);
        gestureRef.current = { type: 'select' };
      } else if (canAddMore) {
        const color = getColorAtPoint(x, y);
        const newId = Date.now();
        setMarkers(prev => [...prev, { id: newId, x, y, color }]);
        setActiveMarker({
          id: newId,
          startX: e.clientX,
          startY: e.clientY,
          moved: false,
          isNew: true
        });
        setDragPreviewColor(color);
        gestureRef.current = { type: 'select' };
      }
    }
  }, [screenToCanvas, findMarkerAtPoint, canAddMore, getColorAtPoint, cancelSelection, getPointersDistance, getPointersCenter, zoom, pan]);

  const handlePointerMove = useCallback((e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    e.preventDefault();
    
    // Update pointer position
    pointersRef.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
    
    const gesture = gestureRef.current;
    if (!gesture) return;
    
    // Desktop pan (Ctrl/Cmd + drag)
    if (gesture.type === 'pan') {
      const dx = e.clientX - gesture.startX;
      const dy = e.clientY - gesture.startY;
      const newPan = clampPan({
        x: gesture.startPan.x + dx,
        y: gesture.startPan.y + dy
      }, zoom);
      setPan(newPan);
      return;
    }
    
    // Mobile pinch/pan gesture
    if (gesture.type === 'pinch' && pointersRef.current.size === 2) {
      const currentDistance = getPointersDistance();
      const currentCenter = getPointersCenter();
      
      const distanceDelta = currentDistance - gesture.startDistance;
      let newZoom = gesture.startZoom + distanceDelta * 0.01;
      newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
      
      const panDeltaX = currentCenter.x - gesture.startCenter.x;
      const panDeltaY = currentCenter.y - gesture.startCenter.y;
      
      const newPan = clampPan({
        x: gesture.startPan.x + panDeltaX,
        y: gesture.startPan.y + panDeltaY
      }, newZoom);
      
      setZoom(newZoom);
      setPan(newPan);
      return;
    }
    
    // Selection gesture
    if (gesture.type === 'select' && activeMarker) {
      const { x, y } = screenToCanvas(e.clientX, e.clientY);
      const margin = HIT_RADIUS / zoom;
      const clampedX = Math.max(margin, Math.min(x, canvasSize.width - margin));
      const clampedY = Math.max(margin, Math.min(y, canvasSize.height - margin));
      
      if (!activeMarker.moved) {
        const dx = e.clientX - activeMarker.startX;
        const dy = e.clientY - activeMarker.startY;
        if (Math.sqrt(dx * dx + dy * dy) > 5) {
          setActiveMarker(prev => prev ? { ...prev, moved: true } : null);
        }
      }
      
      const newColor = getColorAtPoint(clampedX, clampedY);
      setMarkers(prev => prev.map(m =>
        m.id === activeMarker.id
          ? { ...m, x: clampedX, y: clampedY, color: newColor }
          : m
      ));
      setDragPreviewColor(newColor);
    }
  }, [activeMarker, screenToCanvas, canvasSize, getColorAtPoint, zoom, getPointersDistance, getPointersCenter, clampPan]);

  const handlePointerUp = useCallback((e) => {
    pointersRef.current.delete(e.pointerId);
    
    const gesture = gestureRef.current;
    
    // End pan gesture
    if (gesture?.type === 'pan') {
      gestureRef.current = null;
      return;
    }
    
    // End pinch gesture
    if (gesture?.type === 'pinch') {
      if (pointersRef.current.size < 2) {
        gestureRef.current = null;
      }
      return;
    }
    
    // End selection gesture
    if (gesture?.type === 'select' && activeMarker) {
      if (!activeMarker.moved && !activeMarker.isNew) {
        setMarkers(prev => prev.filter(m => m.id !== activeMarker.id));
      }
      setActiveMarker(null);
      setDragPreviewColor(null);
      gestureRef.current = null;
    }
  }, [activeMarker]);

  const handlePointerCancel = useCallback((e) => {
    pointersRef.current.delete(e.pointerId);
    cancelSelection();
    gestureRef.current = null;
  }, [cancelSelection]);

  const handleListRemove = useCallback((markerId) => {
    setMarkers(prev => prev.filter(m => m.id !== markerId));
  }, []);

  const handleConfirm = () => {
    const newColors = markers.map(m => m.color).reverse();
    onConfirm(newColors);
  };

  useEffect(() => {
    const handleResize = () => {
      if (imgRef.current?.complete) drawImage();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawImage]);

  // Get marker display style
  const getMarkerStyle = useCallback((marker, isDragging) => {
    const canvas = canvasRef.current;
    if (!canvas) return {};
    
    const displayX = marker.x;
    const displayY = marker.y;
    
    const baseRadius = isDragging ? MARKER_RADIUS_ACTIVE : MARKER_RADIUS_IDLE;
    const radius = baseRadius / zoom;
    
    return {
      width: radius * 2,
      height: radius * 2,
      left: displayX - radius,
      top: displayY - radius,
      borderWidth: Math.max(2, 4 / zoom),
    };
  }, [zoom]);

  return (
    <div
      className="fixed inset-0 bg-charcoal/80 flex flex-col z-[60]"
      data-testid="multi-color-picker-modal"
    >
      {/* Header */}
      <div className="bg-white border-b border-beige px-4 py-2 flex items-center justify-between shrink-0">
        <div>
          <h3 className="text-lg font-medium text-charcoal">Pick Colors</h3>
        </div>
        <div className="flex items-center gap-2">
          {zoom > 1 && (
            <>
              <span className="text-xs font-medium px-2 py-1 bg-gray-100 rounded">
                {zoom.toFixed(1)}x
              </span>
              <button
                onClick={handleResetZoom}
                className="p-2 text-sage hover:text-sage-dark hover:bg-sage/10 rounded-lg"
                title="Reset zoom"
              >
                <RotateCcw size={18} />
              </button>
            </>
          )}
          <button onClick={onClose} className="p-2 text-gray-soft hover:text-charcoal rounded-lg">
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Gesture help text - different for mobile vs desktop */}
      <div className="bg-white border-b border-beige px-4 py-2 shrink-0">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-soft justify-center items-center">
          {isTouchDevice ? (
            <>
              <span><strong className="text-charcoal">Tap</strong> to add color</span>
              <span><strong className="text-charcoal">Drag</strong> marker to move</span>
              <span><strong className="text-charcoal">Tap</strong> marker to remove</span>
              <span><strong className="text-charcoal">Pinch</strong> to zoom</span>
              <span><strong className="text-charcoal">2-finger drag</strong> to pan</span>
            </>
          ) : (
            <>
              <span><strong className="text-charcoal">Click</strong> to add color</span>
              <span><strong className="text-charcoal">Drag</strong> marker to move</span>
              <span><strong className="text-charcoal">Click</strong> marker to remove</span>
              <span><strong className="text-charcoal">Scroll</strong> to zoom</span>
              <span className="inline-flex items-center gap-1">
                {isMac ? (
                  <span className="inline-flex items-center gap-0.5 text-charcoal font-bold">
                    <Command size={12} strokeWidth={2.5} />
                  </span>
                ) : (
                  <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-[10px] font-mono text-charcoal">Ctrl</kbd>
                )}
                <span>+ drag to pan</span>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Drag preview - floating overlay */}
      {activeMarker && dragPreviewColor && (
        <div className="absolute left-4 right-4 top-28 z-50 pointer-events-none">
          <div className="mx-auto max-w-xs px-4 py-2 rounded-lg flex items-center gap-3 bg-white/95 shadow-lg border border-sage/30">
            <div
              className="w-10 h-10 rounded-lg border-2 border-white shadow shrink-0"
              style={{ backgroundColor: dragPreviewColor }}
            />
            <div>
              <p className="text-xs text-sage font-medium">
                {activeMarker.moved ? 'Dragging...' : isTouchDevice ? 'Tap to remove' : 'Click to remove'}
              </p>
              <p className="text-sm font-mono text-charcoal">{dragPreviewColor}</p>
            </div>
          </div>
        </div>
      )}

      {/* Canvas Area */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 flex items-center justify-center bg-gray-100 overflow-hidden"
      >
        <img
          ref={imgRef}
          src={proxyImageUrl || ''}
          alt=""
          className="hidden"
          onLoad={drawImage}
          onError={() => setImageError(true)}
        />

        <div
          ref={wrapperRef}
          className="relative"
          style={{
            touchAction: 'none',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
          }}
        >
          <canvas
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onWheel={handleWheel}
            className="rounded-lg shadow-md block"
            style={{
              cursor: canAddMore ? 'crosshair' : 'not-allowed',
              touchAction: 'none',
            }}
          />

          {/* Markers */}
          {imageLoaded && markers.map((marker) => {
            const isDragging = activeMarker?.id === marker.id;
            const style = getMarkerStyle(marker, isDragging);

            return (
              <div
                key={marker.id}
                className={`absolute rounded-full border-white shadow-lg pointer-events-none ${
                  isDragging ? 'z-10' : ''
                }`}
                style={{
                  ...style,
                  backgroundColor: marker.color,
                  borderStyle: 'solid',
                  boxShadow: isDragging
                    ? '0 8px 25px rgba(0,0,0,0.4)'
                    : '0 3px 10px rgba(0,0,0,0.25)',
                }}
              />
            );
          })}

          {!imageLoaded && !imageError && (
            <div className="flex items-center justify-center bg-gray-200 rounded-lg min-w-[200px] min-h-[200px]">
              <div className="animate-spin w-8 h-8 border-4 border-sage border-t-transparent rounded-full" />
            </div>
          )}
          {imageError && (
            <div className="flex flex-col items-center justify-center bg-gray-100 rounded-lg min-w-[200px] min-h-[200px] text-center px-4">
              <p className="text-sm text-red-500 mb-2">Unable to load image for color picking.</p>
              <button type="button" onClick={onClose} className="text-sm text-sage hover:text-sage-dark underline">Close</button>
            </div>
          )}
        </div>
      </div>

      {/* Selected Colors - Fixed height with sticky header */}
      <div className="bg-white border-t border-beige shrink-0 h-44 flex flex-col">
        {/* Sticky header */}
        <div className="px-4 py-2 border-b border-beige bg-white shrink-0">
          <h4 className="text-sm font-medium text-charcoal">
            Selected Colors
            <span className="text-xs text-gray-soft font-normal ml-2">
              ({markers.length} picked)
            </span>
          </h4>
        </div>

        {/* Scrollable color list */}
        <div className="flex-1 overflow-auto px-4 py-2">
          {markers.length === 0 ? (
            <p className="text-sm text-gray-soft italic py-4 text-center bg-cream/50 rounded-lg border border-dashed border-beige">
              {isTouchDevice ? 'Tap' : 'Click'} on the image to select colors
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {[...markers].reverse().map((marker, displayIdx) => (
                <div
                  key={marker.id}
                  className="flex items-center gap-2 py-1.5 px-2 bg-cream/50 rounded-lg"
                >
                  <div
                    className="w-8 h-8 rounded-lg border-2 border-white shadow-md shrink-0"
                    style={{ backgroundColor: marker.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-gray-soft leading-tight">
                      {displayIdx === 0 ? 'Latest' : `#${markers.length - displayIdx}`}
                    </p>
                    <p className="text-xs font-mono text-charcoal">{marker.color}</p>
                  </div>
                  <button
                    onClick={() => handleListRemove(marker.id)}
                    className="p-1.5 text-gray-soft hover:text-red-500 hover:bg-red-50 rounded transition-colors shrink-0"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-beige px-4 py-3 flex gap-3 shrink-0">
        <button
          onClick={onClose}
          className="px-4 py-2.5 border border-beige text-charcoal rounded-lg hover:bg-cream"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={markers.length === 0}
          className="flex-1 px-4 py-2.5 bg-sage text-white rounded-lg hover:bg-sage-dark disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Check size={18} />
          Add {markers.length} Color{markers.length !== 1 ? 's' : ''}
        </button>
      </div>
    </div>
  );
};

export default MultiColorPickerModal;
